import React, { useState, useEffect } from 'react';
import ContextualDataPanel from './sidebar/ContextualDataPanel';
import DiagnosticSuggestionsPanel from './sidebar/DiagnosticSuggestionsPanel';
import UserActionsPanel from './sidebar/UserActionsPanel';
import StatusIndicator from './sidebar/StatusIndicator';
import './Sidebar.css';

/**
 * Sidebar Component
 * 
 * A modular, context-aware assistant sidebar that displays relevant diagnostic data,
 * suggestions, and user actions dynamically retrieved from the backend.
 * 
 * @param {Object} props - Component props
 * @param {string} props.patientId - Unique patient identifier
 * @param {Object} props.studyData - Study metadata
 * @param {Object} props.imageInfo - Information about the currently displayed image
 * @param {boolean} props.isVisible - Sidebar visibility state
 * @param {Function} props.onToggle - Callback for toggle action
 * @param {Function} props.onCollapsedChange - Callback when collapsed state changes
 * @param {Function} props.onAcceptAnnotations - Callback to draw annotations on images
 */
const Sidebar = ({ patientId, studyData, imageInfo = null, isVisible = true, onToggle, onCollapsedChange, onAcceptAnnotations }) => {
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [findingsData, setFindingsData] = useState(null);
  const [contextualData, setContextualData] = useState(null);
  const [messagesTemplate, setMessagesTemplate] = useState(null);
  const [loadingState, setLoadingState] = useState({
    diagnostic: 'loading',
    contextual: 'loading',
    findings: 'loading'
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState('all'); // 'all', 'context', 'diagnostic', 'actions'

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (onCollapsedChange) {
      onCollapsedChange(!isExpanded);
    }
  }, [isExpanded, onCollapsedChange]);

  // Fetch data from backend
  useEffect(() => {
    if (!patientId) {
      setLoadingState({
        diagnostic: 'no-patient',
        contextual: 'no-patient',
        findings: 'no-patient',
      });
      return;
    }

    setContextualData(studyData);

    const fetchDiagnosticData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/assistant/outputs/${patientId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setDiagnosticData(data);
        setLoadingState(prev => ({ ...prev, diagnostic: 'success' }));
      } catch (error) {
        console.warn('Diagnostic data not available:', error.message);
        setLoadingState(prev => ({ ...prev, diagnostic: 'error' }));
      }
    };

    const fetchFindingsData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/assistant/findings/${patientId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setFindingsData(data);
        setLoadingState(prev => ({ ...prev, findings: 'success' }));
      } catch (error) {
        console.warn('Findings data not available:', error.message);
        setLoadingState(prev => ({ ...prev, findings: 'error' }));
      }
    };

    const fetchMessagesTemplate = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/assistant/message`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        setMessagesTemplate(data);
      } catch (error) {
        console.warn('Messages template not available:', error.message);
      }
    };

    setLoadingState(prev => ({ ...prev, contextual: 'success' }));

    fetchDiagnosticData();
    fetchFindingsData();
    fetchMessagesTemplate();
    setIsExpanded(true);
  }, [patientId, studyData, BACKEND_URL]);

  // Toggle sidebar expansion
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate overall loading state
  const overallStatus = () => {
    const states = Object.values(loadingState);
    if (states.every(s => s === 'success')) return 'ready';
    if (states.some(s => s === 'loading')) return 'loading';
    if (states.every(s => s === 'error' || s === 'no-patient')) return 'error';
    return 'partial';
  };

  // Handle Accept action - draw annotations on images
  const handleAccept = () => {
    if (findingsData && onAcceptAnnotations) {
      console.log('Accept clicked - drawing annotations');
      onAcceptAnnotations(findingsData);
    } else {
      console.log('Accept action - no findings data or callback');
    }
  };

  // Handle Edit action payload from UserActionsPanel
  const handleEdit = ({ label, findings }) => {
    // change local state immediately for responsiveness
    setDiagnosticData((prev) => {
      if (!prev?.patient?.length) {
        return prev;
      }

      const updatedPatient = {
        ...prev.patient[0],
        biradsLabelPhys: label,
        findings,
        diagnosticTextPhys: findings,
      };

      return {
        ...prev,
        patient: [updatedPatient, ...prev.patient.slice(1)],
      };
    });
    // update backend with new diagnostic data
    fetch(`${BACKEND_URL}/api/assistant/outputs/${patientId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patient: [{ biradsLabelPhys: label, diagnosticTextPhys: findings }] })
    })
      .then(data => {
        console.log('Diagnostic text updated successfully:', data);
      })
      .catch(error => {
        console.error('Error updating diagnostic text:', error);
      });
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className={`context-aware-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <h3>AI Assistant</h3>
        </div>

        {/* Status Indicator */}
        {/*
        <StatusIndicator
          status={overallStatus()}
          loadingDetails={loadingState}
        />
        */}

        <div className="sidebar-header-actions">

          {/* Toggle Button (collapse/expand) */}
          <button
            className="sidebar-toggle-btn"
            onClick={handleToggleExpand}
            aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isExpanded ? '→' : '←'}
          </button>
        </div>
      </div>

      {/* Sidebar Content */}
      {isExpanded && (
        <div className="sidebar-content">

          {/* Contextual Data Panel */}
          {(activePanel === 'all' || activePanel === 'context') && (
            <ContextualDataPanel
              contextualData={contextualData}
              imageInfo={imageInfo}
              loadingState={loadingState.contextual}
            />
          )}

          {/* Diagnostic Suggestions Panel */}
          {(activePanel === 'all' || activePanel === 'diagnostic') && (
            <DiagnosticSuggestionsPanel
              patientId={patientId}
              diagnosticData={diagnosticData}
              findingsData={findingsData}
              messagesTemplate={messagesTemplate}
              imageInfo={imageInfo}
              loadingState={loadingState.diagnostic}
            />
          )}

          {/* User Actions Panel */}
          {(activePanel === 'all' || activePanel === 'actions') && (
            <UserActionsPanel
              patientId={patientId}
              diagnosticData={diagnosticData}
              findingsData={findingsData}
              messagesTemplate={messagesTemplate}
              imageInfo={imageInfo}
              onAccept={handleAccept}
              onReject={() => console.log('Reject action')}
              onEdit={handleEdit}
              onExplain={() => console.log('Explain action')}
            />
          )}
        </div>
      )}

      {/* Footer with extensibility indicators */}
      {isExpanded && (
        <div className="sidebar-footer">
          <div className="footer-info">
            <span className="info-icon">ℹ️</span>
            <small>Context-aware assistance enabled</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
